import { useEffect, useMemo, useState } from "react";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import constants from "constants";
import { encodeId } from "utils";
import {
  getEffectiveOwnerId,
  getOfficeNavigationContext,
  isInOfficeNavigationContext,
  OFFICE_NAV_CONTEXT_CHANGE_EVENT,
  resolveOwnerFromOwnerList,
} from "utils/officeNavigationContext";

/**
 * Returns the owner ID that should be used for API calls based on navigation context.
 * Uses the office owner when navigating from a specific office; otherwise uses the
 * global owner switcher selection.
 */
export default function useEffectiveOwnerId() {
  const {
    selectedId: globalOwnerId,
    ownerList,
    commonOwnerData,
    isOwner,
    profile,
  } = useAccountOwnerCommon();
  const [navVersion, setNavVersion] = useState(0);

  useEffect(() => {
    const handleContextChange = () => setNavVersion((v) => v + 1);
    window.addEventListener(OFFICE_NAV_CONTEXT_CHANGE_EVENT, handleContextChange);
    return () =>
      window.removeEventListener(
        OFFICE_NAV_CONTEXT_CHANGE_EVENT,
        handleContextChange,
      );
  }, []);

  const officeContext = useMemo(
    () => getOfficeNavigationContext(),
    [navVersion],
  );

  const isOfficeNavigationContext = useMemo(
    () => isInOfficeNavigationContext(officeContext),
    [officeContext],
  );

  const effectiveOwnerId = useMemo(
    () => getEffectiveOwnerId(globalOwnerId, officeContext),
    [globalOwnerId, officeContext],
  );

  const effectiveOwnerIdString = useMemo(() => {
    if (effectiveOwnerId == null || effectiveOwnerId === "") {
      return "";
    }
    return `${constants.selectedOwnerId}=${encodeId(effectiveOwnerId)}`;
  }, [effectiveOwnerId]);

  const effectiveOwnerData = useMemo(() => {
    if (isOfficeNavigationContext && officeContext?.ownerId != null) {
      const officeOwner = resolveOwnerFromOwnerList(
        ownerList,
        officeContext.ownerId,
      );
      if (officeOwner) return officeOwner;
      return null;
    }
    return isOwner ? profile : commonOwnerData;
  }, [
    isOfficeNavigationContext,
    officeContext?.ownerId,
    ownerList,
    isOwner,
    profile,
    commonOwnerData,
  ]);

  return {
    effectiveOwnerId,
    effectiveOwnerIdString,
    effectiveOwnerData,
    globalOwnerId,
    isOfficeNavigationContext,
    officeContext,
    navVersion,
  };
}
